import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchMailingsLinkList,
  useMailingsCapabilities,
  useMailingsLinkRunStatus,
  useMailingLinkPreview,
  useMailingLinkRecheck,
  useMailingLinkSendAll,
  useMailingLinkTestSend,
  type MailingsCapabilitiesDto,
  type MailingDeliveryFilter,
  type MailingLinkRecipientDto,
  type MailingLinkRunStatusDto,
  type MailingLinkSummaryDto,
} from '../../api/mailings';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Modal, Pagination, Select, Spinner } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { useQueryParams } from '../../lib/query-params';
import {
  AUDIENCE_LABELS,
  DELIVERY_FILTERS,
  EMPTY_LINK_SUMMARY,
  LOGIC_DESCRIPTION_LINK,
  MAILING_TYPE_LABEL_LINK,
  PARAM,
  TRACK_LABELS,
  formatCapabilities,
  formatHistoryLine,
  formatLastSend,
  formatLinkRunProgress,
  parseDeliveryParam,
  parsePageParam,
} from './mailings-model';

/**
 * The SECOND fixed mailing's panel (contract #32.8–#32.13, ADDITIVE v1.9.34,
 * T.Z140): «Рассылка получившим ссылку» over `status=LINK_RECEIVED`.
 *
 * The FIRST fixed panel's controls, one surface over: the stated inclusion
 * logic, the stat-card counters (with «не готовы к отправке» where the first
 * type carries its two buckets), the test letter that never journals, the
 * confirmed paced «Отправить всем», the sandboxed preview and the honest
 * delivery states. THE DIFFERENCE is in every row: the letter's link is the
 * child's own ЕПГУ futurecode page (his programme × his audience), and a child
 * whose link does not resolve is not render-ready at all.
 */

const PAGE_SIZE = 20;

export function LinkReceivedPanel() {
  const { params, setParams } = useQueryParams();
  const delivery = parseDeliveryParam(params[PARAM.DELIVERY] ?? null);
  const page = parsePageParam(params[PARAM.PAGE] ?? null);

  const list = useQuery({
    queryKey: ['mailings-link', 'list', delivery ?? 'ALL', page],
    queryFn: ({ signal }) => fetchMailingsLinkList({ delivery, page, pageSize: PAGE_SIZE }, signal),
  });

  const runStatus = useMailingsLinkRunStatus();

  const summary = list.data?.summary ?? EMPTY_LINK_SUMMARY;
  const rows = list.data?.data ?? [];

  const setFilter = (next: { delivery?: MailingDeliveryFilter }) => {
    setParams({ [PARAM.DELIVERY]: next.delivery ?? null, [PARAM.PAGE]: null });
  };

  return (
    <div className="mailings-classic" data-testid="mailings-link-panel">
      <section className="mailings-desc card" data-testid="mailings-link-logic">
        <div className="mailings-filters mailings-filters--plain">
          <h2>Быстрая рассылка: {MAILING_TYPE_LABEL_LINK}</h2>
        </div>
        <p>{LOGIC_DESCRIPTION_LINK}</p>
      </section>

      <LinkSummaryChips summary={summary} />

      <CapabilitiesLine />

      <TestSendCard rows={rows} />

      <SendAllCard runStatus={runStatus.data?.data ?? null} notSent={summary.delivery.notSent} notReady={summary.notReady} />

      <section className="mailings-list card card--flush" data-testid="mailings-link-list">
        <div className="mailings-filters">
          <h2>Список получателей</h2>
          <Field label="Доставка" htmlFor="mailings-link-delivery">
            <Select
              id="mailings-link-delivery"
              value={delivery ?? ''}
              onChange={(event) => setFilter({ delivery: (event.target.value || undefined) as MailingDeliveryFilter | undefined })}
              data-testid="mailings-link-delivery"
            >
              {DELIVERY_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === ''
                    ? `Все (${summary.total})`
                    : `${option.label} (${summary.delivery[option.countKey]})`}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {list.isPending ? (
          <Spinner label="Загружаем получателей" />
        ) : list.isError ? (
          <ErrorState message={String(list.error)} onRetry={() => void list.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState title="Никого" hint="Под текущие фильтры не попал ни один ребёнок." />
        ) : (
          <RecipientsTable
            rows={rows}
            page={list.data?.meta.page ?? page}
            total={list.data?.meta.total ?? 0}
            onPage={(next) => setParams({ [PARAM.PAGE]: next === 1 ? null : String(next) })}
          />
        )}
      </section>
    </div>
  );
}

/** The accent tone of one summary tile — the first panel's own vocabulary. */
type ChipTone = 'brand' | 'neutral' | 'ok' | 'bad' | 'warn';

/** The counters as stat cards, the first panel's grammar — «не готовы к
 * отправке» where the first type carries its two buckets (T.Z140, the owner's
 * «плюс счётчик не renderReady»). */
function LinkSummaryChips({ summary }: { summary: MailingLinkSummaryDto }) {
  const tiles: ReadonlyArray<{ key: string; value: number; label: string; tone: ChipTone; hint?: string }> = [
    { key: 'total', value: summary.total, label: 'всего получателей', tone: 'brand' },
    { key: 'ready', value: summary.ready, label: 'готовы к отправке', tone: 'ok' },
    {
      key: 'not-ready',
      value: summary.notReady,
      label: 'не готовы к отправке',
      tone: 'warn',
      hint: 'Нет e-mail, нет разобранного имени или не определилась ссылка на курс — письмо такой ребёнок не получит.',
    },
    { key: 'not-sent', value: summary.delivery.notSent, label: 'не получил письмо', tone: 'neutral' },
    { key: 'accepted', value: summary.delivery.accepted, label: 'принято', tone: 'neutral' },
    { key: 'delivered', value: summary.delivery.delivered, label: 'доставлено', tone: 'ok' },
    { key: 'failed', value: summary.delivery.failed, label: 'ошибка доставки', tone: 'bad' },
    { key: 'ai', value: summary.tracks.ai, label: 'программы ИИ', tone: 'brand' },
    { key: 'python', value: summary.tracks.python, label: 'программы Python', tone: 'brand' },
    {
      key: 'unresolved',
      value: summary.tracks.unresolved,
      label: 'трек не определён',
      tone: 'neutral',
      hint: 'Программа не распозналась — ссылки у такого письма нет, ребёнок не получит его.',
    },
  ];
  return (
    <section className="mailings-summary" data-testid="mailings-link-summary">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={`mailings-chip mailings-chip--${tile.tone}`}
          data-testid={`mailings-link-chip-${tile.key}`}
          title={tile.hint}
        >
          <span className="mailings-chip-value">{tile.value}</span>
          <span className="mailings-chip-label">{tile.label}</span>
        </div>
      ))}
    </section>
  );
}

function CapabilitiesLine() {
  const capabilities = useMailingsCapabilities();
  const value: MailingsCapabilitiesDto | undefined = capabilities.data?.data;
  return (
    <p className="mailings-capabilities" data-testid="mailings-link-capabilities">
      {formatCapabilities(value)}
    </p>
  );
}

function TestSendCard({ rows }: { rows: MailingLinkRecipientDto[] }) {
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const testSend = useMailingLinkTestSend();
  const selected = rows.find((row) => row.applicationId === applicationId) ?? rows[0] ?? null;

  const submit = () => {
    if (!selected || !email.trim()) return;
    setResult(null);
    testSend.mutate(
      { applicationId: selected.applicationId, email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}) },
      {
        onSuccess: (answer) => setResult(`Письмо ушло на ${answer.data.to} (ссылка: ${answer.data.startUrl})`),
        onError: (error) => setResult(`Ошибка: ${error.message}`),
      },
    );
  };

  return (
    <section className="mailings-test card" data-testid="mailings-link-test">
      <h2>Письмо тестом</h2>
      <p className="mailings-test-hint">
        Одно письмо на указанный адрес — с именем и ссылкой, как их увидит получатель. Тест не попадает в историю
        рассылки и не мешает «Отправить всем».
      </p>
      <div className="mailings-test-form">
        <Field label="Ребёнок (письмо его данными)" htmlFor="mailings-link-test-child">
          <Select
            id="mailings-link-test-child"
            value={selected ? String(selected.applicationId) : ''}
            onChange={(event) => setApplicationId(Number(event.target.value))}
            data-testid="mailings-link-test-child"
          >
            {rows.map((row) => (
              <option key={row.applicationId} value={row.applicationId}>
                {row.fullName ?? '(без ФИО)'} — {row.startUrl ? 'ссылка определена' : 'нет ссылки'}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Имя в письме" htmlFor="mailings-link-test-name">
          <Input
            id="mailings-link-test-name"
            value={name}
            placeholder={selected?.greetingName ?? 'Имя Отчество'}
            onChange={(event) => setName(event.target.value)}
            data-testid="mailings-link-test-name"
          />
        </Field>
        <Field label="Куда отправить" htmlFor="mailings-link-test-email">
          <Input
            id="mailings-link-test-email"
            type="email"
            value={email}
            placeholder="ваш@ismart.org"
            onChange={(event) => setEmail(event.target.value)}
            data-testid="mailings-link-test-email"
          />
        </Field>
        <Button onClick={submit} disabled={!email.trim() || !selected || testSend.isPending} data-testid="mailings-link-test-send">
          {testSend.isPending ? 'Отправляем…' : 'Отправить письмо тестом'}
        </Button>
      </div>
      {result ? (
        <p className="mailings-test-result" data-testid="mailings-link-test-result">
          {result}
        </p>
      ) : null}
    </section>
  );
}

function SendAllCard({
  runStatus,
  notSent,
  notReady,
}: {
  runStatus: MailingLinkRunStatusDto | null;
  notSent: number;
  notReady: number;
}) {
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const sendAll = useMailingLinkSendAll();
  const running = runStatus?.running ?? false;
  const run = runStatus?.run ?? null;

  const arm = () => {
    setConfirming(false);
    setNotice(null);
    sendAll.mutate(undefined, {
      onSuccess: (answer) => setNotice(`Рассылка запущена: ${answer.data.total} получателей (запуск ${answer.data.runId}).`),
      onError: (error) => setNotice(`Не удалось запустить: ${error.message}`),
    });
  };

  return (
    <section className="mailings-sendall card" data-testid="mailings-link-sendall">
      <h2>Отправить всем</h2>
      <p className="mailings-sendall-hint">
        Письма уходят по очереди, в заданном темпе (по умолчанию 5 в секунду). Запуск продолжается с того места, где остановился: кто
        уже получил письмо — повторно не получает. Не готовы к отправке: {notReady}.
      </p>
      <div className="mailings-sendall-actions">
        <Button variant="primary" disabled={running || sendAll.isPending} onClick={() => setConfirming(true)} data-testid="mailings-link-send-all">
          {running ? 'Отправляется…' : `Отправить всем (${notSent})`}
        </Button>
        <RecheckButton />
      </div>
      {running && run ? (
        <p className="mailings-run-progress" data-testid="mailings-link-run-progress">
          {formatLinkRunProgress(run)}
        </p>
      ) : null}
      {notice ? (
        <p className="mailings-sendall-notice" data-testid="mailings-link-sendall-notice">
          {notice}
        </p>
      ) : null}
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Отправить всем?"
        description={`Будет отправлено ${notSent} писем родителям детей, получивших ссылку на испытание. Тем, кто уже получил письмо, повторно не отправляем.`}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Отмена</Button>
            <Button variant="primary" onClick={arm} data-testid="mailings-link-send-all-confirm">
              Отправить
            </Button>
          </>
        }
      >
        <p>{LOGIC_DESCRIPTION_LINK}</p>
      </Modal>
    </section>
  );
}

function RecheckButton() {
  const recheck = useMailingLinkRecheck();
  const [notice, setNotice] = useState<string | null>(null);
  return (
    <span className="mailings-recheck">
      <Button
        disabled={recheck.isPending}
        onClick={() =>
          recheck.mutate(undefined, {
            onSuccess: (answer) =>
              setNotice(
                `Проверено: ${answer.data.checked}; доставлено: ${answer.data.delivered}; без подтверждения: ${answer.data.unchanged}`,
              ),
            onError: (error) => setNotice(`Не удалось проверить: ${error.message}`),
          })
        }
        data-testid="mailings-link-recheck"
      >
        Проверить доставку
      </Button>
      {notice ? (
        <span className="mailings-recheck-notice" data-testid="mailings-link-recheck-notice">
          {notice}
        </span>
      ) : null}
    </span>
  );
}

/** The recipients table — the first panel's chrome, the second panel's columns:
 * «Готовность» (the render readiness, link included) instead of the contract
 * buckets, plus the «Класс» cell the audience folds from. */
function RecipientsTable({
  rows,
  page,
  total,
  onPage,
}: {
  rows: MailingLinkRecipientDto[];
  page: number;
  total: number;
  onPage: (next: number) => void;
}) {
  const [previewId, setPreviewId] = useState<number | null>(null);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  return (
    <>
      <TableWrap>
        <table className="data mailings-table" data-testid="mailings-link-table">
          <thead>
            <tr>
              <th>Ребёнок</th>
              <th>E-mail</th>
              <th>Класс</th>
              <th>Программа</th>
              <th>Трек</th>
              <th>Готовность</th>
              <th>Отправлено</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.applicationId}
                className="mailings-row"
                onClick={() => setPreviewId(row.applicationId)}
                data-testid="mailings-link-row"
              >
                <td className="cell-primary">{row.fullName ?? '—'}</td>
                <td className="cell-muted">{row.email ?? '—'}</td>
                <td className="cell-compact">{row.classCourse ?? '—'}</td>
                <td className="mailings-program" title={row.programName ?? undefined}>
                  {row.programName ?? '—'}
                </td>
                <td className="cell-compact">
                  {TRACK_LABELS[row.track]}
                  {!row.trackResolved ? <span className="cell-muted"> (не определён)</span> : null}
                </td>
                <td className="cell-compact">
                  {row.renderReady ? (
                    <Badge tone="ok">{row.audience ? `готов · ${AUDIENCE_LABELS[row.audience] ?? row.audience}` : 'готов'}</Badge>
                  ) : (
                    <Badge tone="warn">не готов</Badge>
                  )}
                </td>
                <td className="cell-compact cell-muted">{formatLastSend(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      <Pagination
        meta={{ page, totalPages: pages, total, limit: PAGE_SIZE }}
        onPage={onPage}
        rangeCaption={`Показаны ${from}–${to} из ${total}`}
      />
      <PreviewModal applicationId={previewId} onClose={() => setPreviewId(null)} />
    </>
  );
}

function PreviewModal({ applicationId, onClose }: { applicationId: number | null; onClose: () => void }) {
  const preview = useMailingLinkPreview(applicationId);
  const letter = preview.data?.data ?? null;
  return (
    <Modal open={applicationId !== null} onClose={onClose} title="Предпросмотр письма" wide>
      {preview.isPending || !letter ? (
        <Spinner label="Готовим предпросмотр" />
      ) : (
        <div className="mailings-preview" data-testid="mailings-link-preview">
          <p className="mailings-preview-meta">
            {letter.subject} · трек {TRACK_LABELS[letter.track]}
            {letter.audience ? ` · аудитория ${AUDIENCE_LABELS[letter.audience] ?? letter.audience}` : ''} · ссылка{' '}
            {letter.startUrl || '—'}
          </p>
          {/* The letter is third-party HTML: sandboxed with NO scripts and NO same-origin. */}
          <iframe
            className="mailings-preview-frame"
            title="Предпросмотр письма"
            sandbox=""
            srcDoc={letter.html}
            data-testid="mailings-link-preview-frame"
          />
          <div className="mailings-history">
            <h3>История писем этому ребёнку</h3>
            {letter.history.length === 0 ? (
              <p data-testid="mailings-link-history-empty">Ещё не отправлялось.</p>
            ) : (
              <ul data-testid="mailings-link-history">
                {letter.history.map((message) => (
                  <li key={message.id}>{formatHistoryLine(message)}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
